/**
 * # Bulk Delete Auto-Generated Emails Dialog
 * 
 * UI-komponent for å forhåndsvise og slette automatisk-genererte e-poster
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Badge } from '@/shared/components/ui/badge';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Loader2, Trash2, AlertTriangle, CheckCircle2, XCircle, Bot } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { logDev } from '@/shared/lib/log-dev';

interface AutoEmail {
  id: string;
  from_email: string;
  from_name: string | null;
  subject: string;
  received_at: string;
  detection: {
    isAutomatic: boolean;
    confidence: number;
    reasons: string[];
    category: string;
    shouldDelete: boolean;
  };
}

interface BulkDeleteAutoEmailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: (deletedCount: number) => void;
}

export function BulkDeleteAutoEmailsDialog({
  open,
  onOpenChange,
  onDeleted,
}: BulkDeleteAutoEmailsDialogProps) {
  const { t } = useTranslation('production');
  
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [autoEmails, setAutoEmails] = useState<AutoEmail[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [minConfidence, setMinConfidence] = useState(0.85);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch auto-detected emails when dialog opens
  useEffect(() => {
    if (open) {
      fetchAutoEmails();
    } else {
      // Reset state when dialog closes
      setAutoEmails([]);
      setSelectedIds(new Set());
      setError(null);
      setSuccess(null);
    }
  }, [open, minConfidence]);

  const fetchAutoEmails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `/api/supplier-emails/bulk-delete-auto?minConfidence=${minConfidence}&limit=100`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch auto-generated emails');
      }

      const data = await response.json();
      
      if (data.success) {
        setAutoEmails(data.emails || []);
        // Select all by default
        setSelectedIds(new Set(data.emails.map((e: AutoEmail) => e.id)));
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err: any) {
      logDev.error('[BulkDeleteAutoEmailsDialog] Fetch error', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) {
      setError('No emails selected');
      return;
    }

    setDeleting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/supplier-emails/bulk-delete-auto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailIds: Array.from(selectedIds),
          minConfidence,
          dryRun: false,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete emails');
      }

      const data = await response.json();

      if (data.success) {
        setSuccess(`Successfully deleted ${data.deletedCount} auto-generated emails`);
        
        // Notify parent component
        if (onDeleted) {
          onDeleted(data.deletedCount);
        }

        // Close dialog after 2 seconds
        setTimeout(() => {
          onOpenChange(false);
        }, 2000);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err: any) {
      logDev.error('[BulkDeleteAutoEmailsDialog] Delete error', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === autoEmails.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(autoEmails.map(e => e.id)));
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'cicd':
        return 'bg-blue-500/10 text-blue-500';
      case 'no-reply':
        return 'bg-red-500/10 text-red-500';
      case 'notification':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'marketing':
        return 'bg-purple-500/10 text-purple-500';
      case 'transactional':
        return 'bg-green-500/10 text-green-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      'cicd': 'CI/CD',
      'no-reply': 'No-Reply',
      'notification': 'Notification',
      'marketing': 'Marketing',
      'transactional': 'Transactional',
      'generated': 'Generated',
    };
    return labels[category] || category;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-blue-500" />
            Delete Automated System Emails
          </DialogTitle>
          <DialogDescription>
            <div className="space-y-2">
            <p>
              Review and delete emails from <strong>automated systems only</strong> - not spam or questionable emails.
            </p>
            <p className="text-xs">
              ✅ Deletes: GitHub notifications, Vercel deployments, no-reply addresses, marketing automation<br />
              ❌ Never deletes: Real supplier emails, sales inquiries, quotations, or human-written messages
            </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Controls */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Safety Level:</label>
              <select
                value={minConfidence}
                onChange={(e) => setMinConfidence(parseFloat(e.target.value))}
                className="px-3 py-1 border rounded-md text-sm"
                disabled={loading || deleting}
              >
                <option value="0.95">Very Safe (95%+ confidence only)</option>
                <option value="0.90">Safe (Recommended - 90%+)</option>
                <option value="0.85">Standard (85%+ - GitHub/Vercel/no-reply)</option>
              </select>
              <span className="text-xs text-muted-foreground ml-2">
                Higher = safer, fewer emails deleted
              </span>
            </div>

            {autoEmails.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                disabled={deleting}
              >
                {selectedIds.size === autoEmails.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>

          {/* Status Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-md">
              <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-md">
              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
              <p className="text-sm text-green-500">{success}</p>
            </div>
          )}

          {/* Email List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : autoEmails.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
              <p className="text-lg font-medium">No auto-generated emails found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your inbox is clean! All emails appear to be from real people.
              </p>
            </div>
          ) : (
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-2">
                {autoEmails.map((email) => (
                  <div
                    key={email.id}
                    className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <Checkbox
                      checked={selectedIds.has(email.id)}
                      onCheckedChange={() => toggleSelection(email.id)}
                      disabled={deleting}
                      className="mt-1"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {email.subject || 'No subject'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {email.from_name || email.from_email}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge className={getCategoryColor(email.detection.category)}>
                            {getCategoryLabel(email.detection.category)}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {Math.round(email.detection.confidence * 100)}%
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mt-2">
                        {email.detection.reasons.slice(0, 2).map((reason, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-0.5 bg-muted rounded-full"
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-muted-foreground">
              {selectedIds.size} of {autoEmails.length} selected
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={selectedIds.size === 0 || deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete {selectedIds.size} Email{selectedIds.size !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

